import React, { useState, useMemo } from 'react';
import { ArrowLeft, Upload, CheckCircle, XCircle, Loader2, Save, Trash2, RefreshCw, AlertCircle, Edit3 } from 'lucide-react';
import pb from '../lib/pocketbaseClient';
import { PLATFORMS, CATEGORIES, slugify } from '../lib/storeApi';

const SCRAPER_BASE = '/api/scraper';

// Same image in different sizes (._SX679_. / ._SY88_.) = duplicate. Keep one full-size URL.
const imgKey = (u) => String(u).split('?')[0].replace(/\._[^/]*_\.(jpg|jpeg|png|webp)$/i, '.$1');
const uniqueImages = (arr) => {
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  const out = [];
  for (const u of arr) {
    if (typeof u !== 'string' || !/^https?:\/\//i.test(u)) continue;
    const k = imgKey(u);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
};

const detectPlatform = (url) => {
  if (!url) return '';
  if (/amazon\./i.test(url)) return 'amazon';
  if (/flipkart\./i.test(url)) return 'flipkart';
  if (/meesho\./i.test(url)) return 'meesho';
  if (/myntra\./i.test(url)) return 'myntra';
  return '';
};

export default function BulkStoreUpload({ onClose, onSaved }) {
  const [input, setInput] = useState('');
  const [defaultCategory, setDefaultCategory] = useState('');
  const [defaultActive, setDefaultActive] = useState(true);
  const [defaultFeatured, setDefaultFeatured] = useState(false);
  const [autoSlug, setAutoSlug] = useState(true);

  const [rows, setRows] = useState([]);       // [{url, status, data, error, editMode}]
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  const parsedUrls = useMemo(() => {
    return input.split('\n').map(s => s.trim()).filter(s => /^https?:\/\//i.test(s));
  }, [input]);

  const updateRow = (idx, patch) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const updateRowData = (idx, patch) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, data: { ...r.data, ...patch } } : r));
  };

  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

  const handleScrapeAll = async () => {
    if (!parsedUrls.length) { alert('কোনো valid URL পাইনি। প্রতি লাইনে একটা full URL দাও (https://...)'); return; }

    setRows(parsedUrls.map(url => ({
      url, status: 'pending', data: null, error: null,
      platform: detectPlatform(url),
    })));
    setProcessing(true);

    for (let i = 0; i < parsedUrls.length; i++) {
      const url = parsedUrls[i];
      updateRow(i, { status: 'scraping' });

      try {
        const res = await fetch(`${SCRAPER_BASE}/scrape?url=${encodeURIComponent(url)}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        if (json.ok && json.data) {
          const d = json.data;
          const platform = json.platform || detectPlatform(url);
          updateRow(i, {
            status: 'done',
            platform,
            data: {
              title: d.title || '',
              slug: autoSlug ? (d.slug || slugify(d.title || '')) : '',
              brand: d.brand || '',
              shortDescription: d.shortDescription || '',
              description: d.description || '',
              price: d.price || '',
              mrp: d.mrp || '',
              rating: d.rating || '',
              reviewCount: d.reviewCount || '',
              category: defaultCategory || d.category || '',
              subCategory: d.subCategory || '',
              affiliateUrl: url,
              platform,
              currency: 'INR',
              tags: d.tags || [],
              features: Array.isArray(d.features) ? d.features : [],
              images: Array.isArray(d.images) ? d.images.slice(0, 8) : [],
              deliveryInfo: '',
              returnPolicy: '',
              isActive: defaultActive,
              isFeatured: defaultFeatured,
              displayOrder: 0,
            },
          });
        } else {
          updateRow(i, { status: 'error', error: json.error || 'Scrape failed' });
        }
      } catch (err) {
        updateRow(i, { status: 'error', error: err.message || 'Network error' });
      }

      // small delay to be polite
      await new Promise(r => setTimeout(r, 400));
    }

    setProcessing(false);
  };

  const handleSaveAll = async () => {
    const valid = rows.filter(r => r.status === 'done' && r.data && r.data.title && r.data.affiliateUrl);
    if (!valid.length) { alert('No valid scraped rows.'); return; }

    setSaving(true);
    let saved = 0;
    let failed = 0;

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (r.status !== 'done' || !r.data) continue;
      const d = r.data;

      const t = (d.title || '').toLowerCase();
      if (t === 'amazon.in' || t === 'amazon.com' || t.includes('online shopping india') || t.includes('access denied') || t === 'page not found') {
        updateRow(i, { status: 'done', error: 'Page not detected — try different URL' });
        failed++;
        continue;
      }

      try {
        // Build JSON payload (like curl test that works)
        const payload = {
          title: (d.title || 'Untitled').slice(0, 290),
          slug: ((d.slug || slugify(d.title || 'product')) + '-' + Date.now().toString().slice(-6)).slice(0, 290),
          brand: d.brand || '',
          shortDescription: (d.shortDescription || '').slice(0, 490),
          description: d.description || '',
          currency: d.currency || 'INR',
          category: d.category || '',
          subCategory: d.subCategory || '',
          platform: d.platform || 'amazon',
          affiliateUrl: d.affiliateUrl,
          deliveryInfo: d.deliveryInfo || '',
          returnPolicy: d.returnPolicy || '',
          isActive: d.isActive !== false,
          isFeatured: d.isFeatured === true,
          tags: Array.isArray(d.tags) ? d.tags : [],
          features: Array.isArray(d.features) ? d.features : [],
          specifications: [],
          highlights: [],
          reviews: [],
          priceSource: d.platform || 'amazon',
          imageUrls: Array.isArray(d.images) ? d.images.slice(0, 6) : [],
        };

        payload.price = Number(d.price) || 0;  // REQUIRED — always send
        payload.mrp = Number(d.mrp) || 0;
        payload.rating = Number(d.rating) || 0;
        payload.reviewCount = Number(d.reviewCount) || 0;

        console.log('[save] sending', payload.title.slice(0, 40), 'url:', payload.affiliateUrl);

        await pb.collection('store_products').create(payload);
        saved++;
        updateRow(i, { status: 'saved' });
      } catch (err) {
        const _fullErr = err?.response?.data || err?.data || err?.message || String(err);
        console.error('[FULL PB ERROR]', JSON.stringify(_fullErr, null, 2));
        // Extract PB field error if any
        let msg = err.message || 'unknown';
        if (err.response && err.response.data) {
          msg = JSON.stringify(err.response.data).slice(0, 200);
        }
        failed++;
        updateRow(i, { status: 'done', error: 'Save failed: ' + msg });
      }
    }

    setSaving(false);
    setSavedCount(saved);
    alert('✅ ' + saved + ' saved. ' + (failed ? '❌ ' + failed + ' failed.' : ''));
    if (onSaved) onSaved();
  };

  const stats = useMemo(() => ({
    total: rows.length,
    done: rows.filter(r => r.status === 'done').length,
    saved: rows.filter(r => r.status === 'saved').length,
    errors: rows.filter(r => r.status === 'error').length,
  }), [rows]);

  const canScrape = parsedUrls.length > 0 && !processing && !saving;
  const canSave = rows.some(r => r.status === 'done') && !saving && !processing;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <button onClick={onClose} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-amber-600 mb-4">
        <ArrowLeft size={14} /> Back to Store Manager
      </button>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 mb-5">
        <h1 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
          <Upload className="text-amber-500" /> Bulk Upload Products
        </h1>
        <p className="text-sm text-gray-500 mb-5">
          একাধিক affiliate link paste করো (প্রতি লাইনে একটি)। URL থেকে title, price, images, description — সব auto-fetch হবে।
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Affiliate URLs ({parsedUrls.length} valid)
            </label>
            <textarea
              rows={8}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={`https://www.amazon.in/dp/B09N3ZNHTY\nhttps://www.flipkart.com/boat-airdopes-141/p/itm6a5eebd4ce32f3\nhttps://www.meesho.com/...\nhttps://www.myntra.com/...`}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
            />
            <p className="text-[11px] text-gray-400 mt-1">প্রতি লাইনে একটি full URL (https:// দিয়ে শুরু)</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Default Category (সব products-এ apply হবে, override করা যাবে)
              </label>
              <select value={defaultCategory} onChange={e => setDefaultCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="">Auto-detect (title থেকে)</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-2 justify-end">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={defaultActive} onChange={e => setDefaultActive(e.target.checked)} />
                <span>Active (public store-এ দেখাবে)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={defaultFeatured} onChange={e => setDefaultFeatured(e.target.checked)} />
                <span>⭐ Featured</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={autoSlug} onChange={e => setAutoSlug(e.target.checked)} />
                <span>Auto-generate slug</span>
              </label>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button onClick={handleScrapeAll} disabled={!canScrape}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-xl text-sm">
              {processing ? <><Loader2 size={14} className="animate-spin" /> Scraping {stats.done + stats.errors}/{stats.total}...</>
                          : <><RefreshCw size={14} /> Fetch All ({parsedUrls.length})</>}
            </button>
            <button onClick={handleSaveAll} disabled={!canSave}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-xl text-sm">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</>
                      : <><Save size={14} /> Save All ({stats.done} ready)</>}
            </button>
            {rows.length > 0 && !processing && !saving && (
              <button onClick={() => { setRows([]); setSavedCount(0); }}
                className="inline-flex items-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-600 font-medium px-4 py-2.5 rounded-xl text-sm">
                <Trash2 size={14} /> Clear
              </button>
            )}
          </div>

          {stats.total > 0 && (
            <div className="flex gap-4 text-sm text-gray-600 pt-2 border-t">
              <span>Total: <b>{stats.total}</b></span>
              <span className="text-green-600">Fetched: <b>{stats.done + stats.saved}</b></span>
              <span className="text-emerald-600">Saved: <b>{stats.saved}</b></span>
              <span className="text-red-500">Failed: <b>{stats.errors}</b></span>
            </div>
          )}
        </div>
      </div>

      {rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="text-sm font-bold text-gray-900">Preview & Edit</h2>
            <p className="text-xs text-gray-500">Auto-fetched data দেখে নাও। কিছু ভুল থাকলে inline edit করো।</p>
          </div>

          <div className="divide-y divide-gray-100">
            {rows.map((r, i) => (
              <RowItem key={i} row={r} index={i} onUpdate={(p) => updateRow(i, p)} onUpdateData={(p) => updateRowData(i, p)} onRemove={() => removeRow(i)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RowItem({ row, index, onUpdate, onUpdateData, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const statusBadge = {
    pending:  { color: 'bg-gray-100 text-gray-500',    icon: null,                    label: 'Pending' },
    scraping: { color: 'bg-blue-100 text-blue-600',    icon: <Loader2 size={11} className="animate-spin" />, label: 'Fetching...' },
    done:     { color: 'bg-green-100 text-green-700',  icon: <CheckCircle size={11} />, label: 'Ready' },
    error:    { color: 'bg-red-100 text-red-600',      icon: <XCircle size={11} />,   label: 'Failed' },
    saved:    { color: 'bg-emerald-100 text-emerald-700', icon: <CheckCircle size={11} />, label: 'Saved' },
  }[row.status] || { color: 'bg-gray-100 text-gray-500', icon: null, label: row.status };

  const d = row.data || {};
  const img = (d.images && d.images[0]) || '';

  return (
    <div className="p-4 hover:bg-gray-50/50">
      <div className="flex items-start gap-3">
        {/* Thumbnail */}
        <div className="w-14 h-14 rounded-lg bg-gray-50 border border-gray-100 overflow-hidden shrink-0">
          {img ? <img src={img} alt="" className="w-full h-full object-contain p-1" onError={(e) => { e.target.style.display = 'none'; }} />
               : <div className="w-full h-full flex items-center justify-center text-gray-300 text-[10px]">No img</div>}
        </div>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${statusBadge.color}`}>
              {statusBadge.icon} {statusBadge.label}
            </span>
            {row.platform && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                via {row.platform}
              </span>
            )}
            <span className="text-[10px] text-gray-400">#{index + 1}</span>
          </div>

          {row.status === 'error' && (
            <div className="flex items-start gap-1.5 text-xs text-red-600">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span>{row.error}</span>
            </div>
          )}

          {row.status === 'pending' || row.status === 'scraping' ? (
            <p className="text-xs text-gray-400 truncate font-mono">{row.url}</p>
          ) : d.title ? (
            <p className="text-sm font-medium text-gray-900 line-clamp-1">{d.title}</p>
          ) : null}

          {d.price !== '' && d.price != null && (
            <p className="text-xs text-gray-500 mt-0.5">
              ₹{d.price} {d.mrp ? <span className="line-through text-gray-400 ml-1">₹{d.mrp}</span> : null}
              {d.rating ? ` • ⭐ ${d.rating}` : ''}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {row.status === 'done' && (
            <button onClick={() => setExpanded(!expanded)}
              className="p-2 rounded-lg hover:bg-indigo-50 text-indigo-500" title="Edit">
              <Edit3 size={14} />
            </button>
          )}
          {row.status !== 'saved' && (
            <button onClick={onRemove} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Remove">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Expandable edit */}
      {expanded && row.status === 'done' && (
        <div className="mt-3 pl-0 sm:pl-16 space-y-2">
          <div className="grid sm:grid-cols-2 gap-2">
            <Field label="Title">
              <input value={d.title || ''} onChange={e => onUpdateData({ title: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs" />
            </Field>
            <Field label="Brand">
              <input value={d.brand || ''} onChange={e => onUpdateData({ brand: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs" />
            </Field>
            <Field label="Price (₹)">
              <input type="number" value={d.price || ''} onChange={e => onUpdateData({ price: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs" />
            </Field>
            <Field label="MRP (₹)">
              <input type="number" value={d.mrp || ''} onChange={e => onUpdateData({ mrp: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs" />
            </Field>
            <Field label="Rating">
              <input type="number" step="0.1" min="0" max="5" value={d.rating || ''} onChange={e => onUpdateData({ rating: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs" />
            </Field>
            <Field label="Review Count">
              <input type="number" value={d.reviewCount || ''} onChange={e => onUpdateData({ reviewCount: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs" />
            </Field>
            <Field label="Category">
              <select value={d.category || ''} onChange={e => onUpdateData({ category: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs">
                <option value="">—</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Platform">
              <select value={d.platform || 'amazon'} onChange={e => onUpdateData({ platform: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs">
                {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Short Description">
            <textarea rows={2} value={d.shortDescription || ''} onChange={e => onUpdateData({ shortDescription: e.target.value })}
              className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs" />
          </Field>
          <Field label="Affiliate URL">
            <input value={d.affiliateUrl || ''} onChange={e => onUpdateData({ affiliateUrl: e.target.value })}
              className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs font-mono" />
          </Field>
          {Array.isArray(d.images) && d.images.length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-gray-500 mb-1">Images ({d.images.length})</p>
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {d.images.map((u, k) => (
                  <img key={k} src={u} alt="" className="w-12 h-12 object-contain rounded border border-gray-100 bg-white p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-medium text-gray-500 mb-0.5">{label}</span>
      {children}
    </label>
  );
}
